import { Component, inject, Input } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { IRehearsal } from '../i-rehearsal.interface';
import { ReadableDateTimePipe } from '../../pipes/readable-date-time.pipe';
import { RehearsalScene } from '../rehearsal-scene';

@Component({
    selector: 'app-rehearsal-summary',
    templateUrl: './rehearsal-summary.component.html',
    styleUrls: ['./rehearsal-summary.component.scss'],
    standalone: false
})
export class RehearsalSummaryComponent {
  @Input() public rehearsal: IRehearsal;
  @Input() public showHeader: boolean = true;

  protected copyButtonText = 'Kopieer Samenvatting';

  /**
   * Checks if a scene has any required items.
   */
  public hasRequiredItems(scene: RehearsalScene): boolean {
    return scene.items?.some(item => item.required) ?? false;
  }
  private toastController = inject(ToastController);
  private dateTimePipe = new ReadableDateTimePipe();

  /**
   * Formats the rehearsal data as WhatsApp-friendly plain text.
   * Uses simple formatting: dashes for bullets, line breaks for sections.
   */
  public formatForWhatsApp(): string {
    if (!this.rehearsal) {
      return '';
    }

    const lines: string[] = [];

    // Add date header
    if (this.rehearsal.performance?.datetime) {
      const dateStr = this.dateTimePipe.transform(this.rehearsal.performance.datetime, 'date');
      lines.push(`Repetitie ${dateStr}`);
      lines.push('');
    }

    // Add purpose if present
    if (this.rehearsal.purpose?.trim()) {
      lines.push(`Repetitie voor: ${this.rehearsal.purpose.trim()}`);
      lines.push('');
    }

    // Add scenes as bullet list
    if (this.rehearsal.scenes?.length > 0) {
      lines.push('Scènes:');
      for (const scene of this.rehearsal.scenes) {
        let sceneLine = `- ${scene.name}`;

        // Add scene notes inline if short, otherwise on new line
        if (scene.notes?.trim()) {
          const notes = scene.notes.trim();
          if (notes.length <= 40) {
            sceneLine += ` (${notes})`;
          } else {
            lines.push(sceneLine);
            sceneLine = `  ${notes}`;
          }
        }
        lines.push(sceneLine);

        // Add required items as sub-bullets
        const requiredItems = scene.items?.filter(item => item.required) || [];
        if (requiredItems.length > 0) {
          for (const item of requiredItems) {
            lines.push(`  • ${item.name}`);
          }
        }
      }
      lines.push('');
    }

    // Add general notes
    if (this.rehearsal.notes?.trim()) {
      lines.push(this.rehearsal.notes.trim());
      lines.push('');
    }

    return lines.join('\n').trim();
  }

  /**
   * Copies the WhatsApp-formatted text to clipboard and shows feedback.
   */
  public async copyToClipboard(): Promise<void> {
    const text = this.formatForWhatsApp();

    try {
      await navigator.clipboard.writeText(text);
      this.copyButtonText = 'Gekopieerd!';

      const toast = await this.toastController.create({
        message: 'Tekst gekopieerd naar klembord',
        duration: 2000,
        color: 'success',
        position: 'bottom'
      });
      await toast.present();

      // Reset button text after delay
      setTimeout(() => {
        this.copyButtonText = 'Kopieer';
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      const toast = await this.toastController.create({
        message: 'Kopiëren mislukt. Probeer opnieuw.',
        duration: 2000,
        color: 'danger',
        position: 'bottom'
      });
      await toast.present();
    }
  }
}
